import { post } from "../../utils/Backend";
import {
  mockGetUserSignalSettings,
  mockListConceptStockScores,
  mockListDailyStockScores,
  mockListDropdownStrategies,
  mockListHoldingsScores,
  mockListMarketStrategies,
  mockListSelectableConcepts,
  mockListSignalIconOptions,
  mockListStockDayKlines,
  mockListTopScores,
  mockListWatchlistScores,
  mockSaveUserSignalSettings,
} from "./TacticsMock";
import type {
  TacticsDailyScorePointDTO,
  TacticsDropdownStrategiesResultDTO,
  TacticsSelectableConceptDTO,
  TacticsSignalIconOptionDTO,
  TacticsStockKlineBarDTO,
  TacticsStockScoreRowDTO,
  TacticsStrategyBriefDTO,
  TacticsTopScoresResultDTO,
  TacticsUserSignalSettingsDTO,
} from "./TacticsTypes";

/** 改为 `mock && false` 可走真实网关（须在应用层接通） */
export const mock = true && false;

const BASE = "/lark/app/tactics";

function assertResult<T>(res: T | string): T {
  if (typeof res === "string") {
    throw new Error(res);
  }
  return res;
}

export const TacticsApi = {
  listDropdownStrategies: async (
    spaceId: string,
    spaceProductId: string
  ): Promise<TacticsDropdownStrategiesResultDTO> => {
    if (mock) return mockListDropdownStrategies();
    const raw = await post<
      { spaceId: string; spaceProductId: string },
      TacticsDropdownStrategiesResultDTO
    >(`${BASE}/listDropdownStrategies`, { spaceId, spaceProductId }, { silent: true });
    return assertResult(raw);
  },

  listMarketStrategies: async (
    spaceId: string,
    spaceProductId: string
  ): Promise<TacticsStrategyBriefDTO[]> => {
    if (mock) return mockListMarketStrategies();
    const raw = await post<{ spaceId: string; spaceProductId: string }, TacticsStrategyBriefDTO[]>(
      `${BASE}/listMarketStrategies`,
      { spaceId, spaceProductId },
      { silent: true }
    );
    return assertResult(raw);
  },

  listTopScores: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string,
    dataDate?: string
  ): Promise<TacticsTopScoresResultDTO> => {
    if (mock) return mockListTopScores(dataDate);
    const body: {
      spaceId: string;
      spaceProductId: string;
      strategyId: string;
      dataDate?: string;
    } = { spaceId, spaceProductId, strategyId };
    const d = dataDate?.trim();
    if (d) {
      body.dataDate = d;
    }
    const raw = await post<
      { spaceId: string; spaceProductId: string; strategyId: string; dataDate?: string },
      TacticsTopScoresResultDTO
    >(`${BASE}/listTopScores`, body, { silent: true });
    return assertResult(raw);
  },

  listWatchlistScores: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string
  ): Promise<TacticsStockScoreRowDTO[]> => {
    if (mock) return mockListWatchlistScores();
    const raw = await post<
      { spaceId: string; spaceProductId: string; strategyId: string },
      TacticsStockScoreRowDTO[]
    >(`${BASE}/listWatchlistScores`, { spaceId, spaceProductId, strategyId }, { silent: true });
    return assertResult(raw);
  },

  listHoldingsScores: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string
  ): Promise<TacticsStockScoreRowDTO[]> => {
    if (mock) return mockListHoldingsScores();
    const raw = await post<
      { spaceId: string; spaceProductId: string; strategyId: string },
      TacticsStockScoreRowDTO[]
    >(`${BASE}/listHoldingsScores`, { spaceId, spaceProductId, strategyId }, { silent: true });
    return assertResult(raw);
  },

  listSelectableConcepts: async (
    spaceId: string,
    spaceProductId: string
  ): Promise<TacticsSelectableConceptDTO[]> => {
    if (mock) return mockListSelectableConcepts();
    const raw = await post<
      { spaceId: string; spaceProductId: string },
      TacticsSelectableConceptDTO[]
    >(`${BASE}/listSelectableConcepts`, { spaceId, spaceProductId }, { silent: true });
    return assertResult(raw);
  },

  listConceptStockScores: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string,
    tactikConceptCode: string
  ): Promise<TacticsStockScoreRowDTO[]> => {
    if (mock) return mockListConceptStockScores(tactikConceptCode);
    const raw = await post<
      {
        spaceId: string;
        spaceProductId: string;
        strategyId: string;
        tactikConceptCode: string;
      },
      TacticsStockScoreRowDTO[]
    >(
      `${BASE}/listConceptStockScores`,
      { spaceId, spaceProductId, strategyId, tactikConceptCode },
      { silent: true }
    );
    return assertResult(raw);
  },

  listSignalIconOptions: async (): Promise<{
    opportunity: TacticsSignalIconOptionDTO[];
    risk: TacticsSignalIconOptionDTO[];
  }> => {
    if (mock) return mockListSignalIconOptions();
    const raw = await post<
      Record<string, never>,
      { opportunity: TacticsSignalIconOptionDTO[]; risk: TacticsSignalIconOptionDTO[] }
    >(`${BASE}/listSignalIconOptions`, {}, { silent: true });
    return assertResult(raw);
  },

  getUserSignalSettings: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string
  ): Promise<TacticsUserSignalSettingsDTO | null> => {
    if (mock) return mockGetUserSignalSettings(strategyId);
    const sid = strategyId.trim();
    const raw = await post<
      { spaceId: string; spaceProductId: string; strategyId: string },
      TacticsUserSignalSettingsDTO | null
    >(
      `${BASE}/getUserSignalSettings`,
      { spaceId, spaceProductId, strategyId: sid },
      { silent: true }
    );
    if (typeof raw === "string") {
      throw new Error(raw);
    }
    return raw;
  },

  recordStrategyVisit: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string
  ): Promise<void> => {
    if (mock) return;
    const raw = await post<
      { spaceId: string; spaceProductId: string; strategyId: string },
      boolean
    >(`${BASE}/recordStrategyVisit`, { spaceId, spaceProductId, strategyId }, { silent: true });
    assertResult(raw);
  },

  resolveDefaultStrategy: async (
    spaceId: string,
    spaceProductId: string
  ): Promise<string | null> => {
    if (mock) return null;
    const raw = await post<
      { spaceId: string; spaceProductId: string },
      { strategyId: string | null }
    >(`${BASE}/resolveDefaultStrategy`, { spaceId, spaceProductId }, { silent: true });
    const res = assertResult(raw);
    const sid = res.strategyId?.trim();
    return sid || null;
  },

  saveUserSignalSettings: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string,
    body: TacticsUserSignalSettingsDTO
  ): Promise<void> => {
    if (mock) return mockSaveUserSignalSettings(strategyId, body);
    const sid = strategyId.trim();
    const raw = await post<
      { spaceId: string; spaceProductId: string; strategyId: string } & TacticsUserSignalSettingsDTO,
      boolean
    >(
      `${BASE}/saveUserSignalSettings`,
      { spaceId, spaceProductId, strategyId: sid, ...body },
      { silent: true }
    );
    assertResult(raw);
  },

  listDailyStockScores: async (
    spaceId: string,
    spaceProductId: string,
    strategyId: string,
    stockCode: string
  ): Promise<TacticsDailyScorePointDTO[]> => {
    const code = stockCode.trim();
    const sid = strategyId.trim();
    if (!code || !sid) {
      return [];
    }
    if (mock) return mockListDailyStockScores(code);
    const raw = await post<
      {
        spaceId: string;
        spaceProductId: string;
        strategyId: string;
        stockCode: string;
      },
      TacticsDailyScorePointDTO[]
    >(
      `${BASE}/listDailyStockScores`,
      { spaceId, spaceProductId, strategyId: sid, stockCode: code },
      { silent: true }
    );
    const r = assertResult(raw);
    return Array.isArray(r) ? r : [];
  },

  listStockDayKlines: async (
    spaceId: string,
    spaceProductId: string,
    stockCode: string,
    size = 300
  ): Promise<TacticsStockKlineBarDTO[]> => {
    const code = stockCode.trim();
    if (!code) {
      return [];
    }
    if (mock) return mockListStockDayKlines(code);
    const raw = await post<
      { spaceId: string; spaceProductId: string; stockCode: string; size?: number },
      TacticsStockKlineBarDTO[]
    >(
      `${BASE}/listStockDayKlines`,
      { spaceId, spaceProductId, stockCode: code, size },
      { silent: true }
    );
    const r = assertResult(raw);
    return Array.isArray(r) ? r : [];
  },
};
